import { useEffect, useState } from "react";

/** Korisnik je u sustavu isključio animacije — scene i uvodi tada ostaju mirni. */
export const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export interface SceneClock {
  /** Proteklo vrijeme (ms) unutar trenutne petlje. */
  time: number;
  /** Redni broj petlje — kao `key` ponovno montira scenu, pa se ulazna animacija odigra iznova. */
  loop: number;
}

/**
 * Sat animirane scene: vraća proteklo vrijeme unutar petlje od `duration` ms.
 *
 * Scena iz tog broja izvodi sve što prikazuje (koliko je slova upisano, gdje je pokazivač,
 * koji je korak), pa je cijeli tijek na jednom mjestu i nema lanca timeouta koji se razilazi.
 * Uz "smanjeno kretanje" sat stoji na `reducedMotionTime` — obično kadar koji sve objašnjava.
 */
export const useSceneClock = (duration: number, reducedMotionTime: number): SceneClock => {
  const [isReduced] = useState<boolean>(prefersReducedMotion);
  const [clock, setClock] = useState<SceneClock>({ time: 0, loop: 0 });

  useEffect(() => {
    if (isReduced) return;

    const start = performance.now();
    let frame = 0;

    const tick = (now: number) => {
      const elapsed = now - start;
      setClock({ time: elapsed % duration, loop: Math.floor(elapsed / duration) });
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame);
  }, [duration, isReduced]);

  return isReduced ? { time: reducedMotionTime, loop: 0 } : clock;
};
